import React, { useState, useEffect } from 'react';

interface UploadedFile {
  name: string;
  size: number;
  uploadedAt: string;
  uploaderName?: string;
  url?: string;
}

export default function ShareFiles() {
  const [mode, setMode] = useState<'choose' | 'send' | 'receive' | 'manage' | null>('choose');
  const [projectName, setProjectName] = useState('');
  const [slug, setSlug] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createdLink, setCreatedLink] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [previousMode, setPreviousMode] = useState<'send' | 'receive' | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentUploadFile, setCurrentUploadFile] = useState('');

  // Check for devMode query parameter
  const urlParams = new URLSearchParams(window.location.search);
  const devMode = urlParams.get('devMode') === '1';

  // Helper function to load uploaded files
  const loadUploadedFiles = async () => {
    if (!slug) return;

    console.log('Loading files for slug:', slug);
    setLoadingFiles(true);
    try {
      const url = `https://fastfiles-api.b-9f2.workers.dev/api/list-files/${slug}`;
      console.log('Fetching from URL:', url);
      const response = await fetch(url);
      const data = await response.json();
      console.log('List files response:', data);
      if (data.success) {
        setUploadedFiles(data.files || []);
        console.log('Files loaded:', data.files);
      }
    } catch (error) {
      console.error('Failed to load files:', error);
    } finally {
      setLoadingFiles(false);
    }
  };

  // Load files when entering manage mode
  useEffect(() => {
    if (mode === 'manage' && slug) {
      loadUploadedFiles();
    }
  }, [mode]);

  // Check if user is admin
  useEffect(() => {
    const adminToken = localStorage.getItem('adminToken');
    setIsAdmin(!!adminToken);
  }, []);

  const generateSlug = (name: string) => {
    // Add random 6-character prefix to prevent collisions and improve security
    // Format: {random-id}/{readable-name}
    const randomId = Math.random().toString(36).substring(2, 8);
    const readableName = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return `${randomId}/${readableName}`;
  };

  const handleProjectNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setProjectName(name);
    setSlug(generateSlug(name));
  };

  const createLink = async () => {
    if (!projectName || !slug) return;

    setIsCreating(true);
    try {
      const response = await fetch('https://fastfiles-api.b-9f2.workers.dev/api/quick-upload-page', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessName: projectName,
          slug: slug
        })
      });

      const result = await response.json();
      if (result.success) {
        const link = `https://fastfiles.app/files/${result.data.slug}`;
        setCreatedLink(link);
        // IMPORTANT: Update slug to match what server returned
        setSlug(result.data.slug);

        // Store in localStorage
        const stored = JSON.parse(localStorage.getItem('myFileReceivers') || '[]');
        stored.push({
          id: result.data.id,
          slug: result.data.slug,
          name: projectName,
          url: link,
          createdAt: new Date().toISOString(),
          mode: mode
        });
        localStorage.setItem('myFileReceivers', JSON.stringify(stored));
      }
    } catch (error) {
      alert('Failed to create link');
    } finally {
      setIsCreating(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedFiles(Array.from(e.target.files));
    }
  };

  // Threshold for using multipart upload (50MB)
  const MULTIPART_THRESHOLD = 50 * 1024 * 1024;
  // Chunk size for multipart upload (10MB - R2 minimum is 5MB)
  const CHUNK_SIZE = 10 * 1024 * 1024;
  const API_BASE = 'https://fastfiles-api.b-9f2.workers.dev';

  // Upload large files using R2 multipart upload
  const uploadLargeFile = async (
    file: File,
    onProgress: (percent: number) => void
  ): Promise<boolean> => {
    try {
      // 1. Initialize multipart upload
      const createRes = await fetch(`${API_BASE}/api/multipart/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: file.name,
          contentType: file.type,
          businessSlug: slug,
          uploaderName: 'Self',
          uploaderEmail: 'self@upload.com'
        })
      });

      const createData = await createRes.json();
      if (!createData.success) {
        console.error('Failed to init multipart:', createData.error);
        return false;
      }

      const { uploadId, key } = createData;
      const parts: { partNumber: number; etag: string }[] = [];
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

      // 2. Upload chunks
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const partNumber = i + 1;

        const partRes = await fetch(
          `${API_BASE}/api/multipart/upload-part?uploadId=${encodeURIComponent(uploadId)}&key=${encodeURIComponent(key)}&partNumber=${partNumber}`,
          {
            method: 'POST',
            body: chunk
          }
        );

        const partData = await partRes.json();
        if (!partData.success) {
          console.error(`Failed to upload part ${partNumber}:`, partData.error);
          // Abort the upload
          await fetch(`${API_BASE}/api/multipart/abort`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uploadId, key })
          });
          return false;
        }

        parts.push({ partNumber: partData.partNumber, etag: partData.etag });
        onProgress(Math.round(((i + 1) / totalChunks) * 100));
      }

      // 3. Complete multipart upload
      const completeRes = await fetch(`${API_BASE}/api/multipart/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uploadId, key, parts })
      });

      const completeData = await completeRes.json();
      if (!completeData.success) {
        console.error('Failed to complete multipart:', completeData.error);
        return false;
      }

      console.log('Large file upload complete:', file.name, completeData.size);
      return true;
    } catch (error) {
      console.error('Large file upload error:', error);
      return false;
    }
  };

  // Upload small files using regular form upload
  const uploadSmallFile = async (file: File): Promise<boolean> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('uploaderName', 'Self');
    formData.append('uploaderEmail', 'self@upload.com');
    formData.append('businessSlug', slug);

    try {
      const response = await fetch(`${API_BASE}/api/business-upload`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        console.error('Upload failed:', await response.text());
        return false;
      }

      const result = await response.json();
      console.log('Upload successful:', result);
      return true;
    } catch (error) {
      console.error('Upload failed:', error);
      return false;
    }
  };

  const uploadFiles = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);
    let allUploadsSuccessful = true;

    console.log('Starting upload with slug:', slug);

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      setCurrentUploadFile(file.name);
      console.log('Uploading file:', file.name, 'size:', file.size, 'to slug:', slug);

      let success: boolean;

      if (file.size > MULTIPART_THRESHOLD) {
        // Large file - use multipart upload
        console.log('Using multipart upload for large file:', file.name);
        success = await uploadLargeFile(file, (chunkProgress) => {
          // Calculate overall progress: completed files + current file progress
          const baseProgress = (i / selectedFiles.length) * 100;
          const fileProgress = (chunkProgress / selectedFiles.length);
          setUploadProgress(Math.round(baseProgress + fileProgress));
        });
      } else {
        // Small file - use regular upload
        success = await uploadSmallFile(file);
        setUploadProgress(Math.round(((i + 1) / selectedFiles.length) * 100));
      }

      if (!success) {
        allUploadsSuccessful = false;
      }
    }

    setIsUploading(false);
    setCurrentUploadFile('');

    if (allUploadsSuccessful) {
      setUploadComplete(true);
      // Load uploaded files to show in manage mode
      await loadUploadedFiles();
    } else {
      alert('Some files failed to upload. Please try again.');
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(createdLink);
    const btn = document.getElementById('copy-btn');
    if (btn) {
      btn.textContent = '✓ Copied!';
      setTimeout(() => { btn.textContent = 'Copy Link'; }, 2000);
    }
  };

  const shareViaEmail = (useGmail = false) => {
    const subject = encodeURIComponent(`Share files: ${projectName}`);
    const body = encodeURIComponent(`I've created a link for sharing files:\n\n${createdLink}\n\nClick the link to upload or download files.`);

    if (useGmail) {
      window.open(`https://mail.google.com/mail/?view=cm&fs=1&su=${subject}&body=${body}`);
    } else {
      window.open(`mailto:?subject=${subject}&body=${body}`);
    }
  };

  const shareViaWhatsApp = () => {
    const text = encodeURIComponent(`Check out my shared files for ${projectName}: ${createdLink}`);
    window.open(`https://wa.me/?text=${text}`);
  };

  const shareViaSMS = () => {
    const text = encodeURIComponent(`Shared files: ${createdLink}`);
    window.open(`sms:?&body=${text}`);
  };

  const showQRCode = () => {
    const qrContainer = document.getElementById('qr-container');
    if (qrContainer) {
      qrContainer.innerHTML = `
        <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onclick="this.remove()">
          <div class="bg-white p-8 rounded-2xl" onclick="event.stopPropagation()">
            <h3 class="text-xl font-bold mb-4">QR Code</h3>
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(createdLink)}" alt="QR Code" />
            <p class="text-sm text-gray-600 mt-4 text-center">Click outside to close</p>
          </div>
        </div>
      `;
    }
  };

  // Component wrapper for dev mode labeling
  const ComponentWrapper = ({ name, children }: { name: string; children: React.ReactNode }) => {
    if (!devMode) return <>{children}</>;

    return (
      <div className="relative" data-component={name}>
        <div className="absolute -top-6 left-0 z-50 px-2 py-1 bg-purple-600 text-white text-xs font-mono rounded-t pointer-events-none">
          {name}
        </div>
        {children}
      </div>
    );
  };

  // Get current component hierarchy
  const getComponentHierarchy = () => {
    const components = [];

    // Main mode component
    if (mode === 'choose') {
      components.push({ name: 'LandingPage', status: 'active' });
      components.push({ name: 'HeroSection', status: 'visible' });
      components.push({ name: 'TrustBadges', status: 'visible' });
      components.push({ name: 'ActionButtons', status: 'visible' });
    } else if (mode === 'send') {
      if (!createdLink) {
        components.push({ name: 'SendMode.NameProject', status: 'active' });
        components.push({ name: 'ProjectNameInput', status: 'visible' });
        components.push({ name: 'SlugPreview', status: projectName ? 'visible' : 'hidden' });
      } else if (!uploadComplete) {
        components.push({ name: 'SendMode.UploadFiles', status: 'active' });
        components.push({ name: 'DragDropZone', status: isDragging ? 'dragging' : 'ready' });
        components.push({ name: 'FileList', status: selectedFiles.length > 0 ? 'visible' : 'hidden' });
        components.push({ name: 'UploadButton', status: isUploading ? 'uploading' : 'ready' });
      } else {
        components.push({ name: 'SendMode.ShareOptions', status: 'active' });
        components.push({ name: 'ShareLinkBox', status: 'visible' });
        components.push({ name: 'ShareButtons', status: 'visible' });
        components.push({ name: 'ViewButtons', status: 'visible' });
      }
    } else if (mode === 'receive') {
      if (!createdLink) {
        components.push({ name: 'ReceiveMode.Setup', status: 'active' });
        components.push({ name: 'ReceiverNameInput', status: 'visible' });
      } else {
        components.push({ name: 'ReceiveMode.Success', status: 'active' });
        components.push({ name: 'ShareInstructions', status: 'visible' });
      }
    } else if (mode === 'manage') {
      components.push({ name: 'ManageMode', status: 'active' });
      components.push({ name: 'FilesList', status: 'visible' });
      components.push({ name: 'BackButton', status: 'visible' });
      components.push({ name: 'RefreshButton', status: 'visible' });
      components.push({ name: 'UploadMoreButton', status: 'visible' });
    }

    return components;
  };

  // Dev Mode Panel Component
  const DevModePanel = () => {
    if (!devMode) return null;

    const components = getComponentHierarchy();

    // Component file paths mapping
    const componentPaths: Record<string, string> = {
      'ShareFiles': 'src/components/ShareFiles.tsx',
      'LandingPage': 'src/components/ShareFiles.tsx:200-350',
      'SendFlow': 'src/components/ShareFiles.tsx:400-600',
      'ReceiveFlow': 'src/components/ShareFiles.tsx:985-1100',
      'ManageView': 'src/components/ShareFiles.tsx:1101-1250',
      'ShareModal': 'src/components/ShareFiles.tsx:880-980',
      'FileUpload': 'src/components/ShareFiles.tsx:700-850',
      'BackButton': 'src/components/ShareFiles.tsx:991',
      'ReceiveTitle': 'src/components/ShareFiles.tsx:996',
      'ReceiverNameInput': 'src/components/ShareFiles.tsx:1006',
      'LinkPreview': 'src/components/ShareFiles.tsx:1010',
      'CreateLinkButton': 'src/components/ShareFiles.tsx:1026',
      'ShareButtons': 'src/components/ShareFiles.tsx:925',
      'PageContainer': 'src/components/ShareFiles.tsx:1105',
      'ManagePanel': 'src/components/ShareFiles.tsx:1120',
      'ShareLinkBox': 'src/components/ShareFiles.tsx:1125',
      'ReceiveModal': 'src/components/ShareFiles.tsx:990',
    };

    return (
      <>
        {/* Animated border effect with matrix rain */}
        <div className="fixed inset-0 pointer-events-none z-50">
          <div className="absolute inset-0 border-4 border-dashed border-yellow-400 opacity-30 animate-pulse"></div>

          {/* Matrix rain effect */}
          <div className="absolute inset-0 overflow-hidden opacity-10">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute text-green-400 text-xs font-mono animate-matrix"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  animationDuration: `${3 + Math.random() * 4}s`
                }}
              >
                {Math.random() > 0.5 ? '1010110' : '</>'}
              </div>
            ))}
          </div>

          <div className="absolute top-0 left-0 w-32 h-32">
            <div className="absolute top-2 left-2 text-yellow-400 text-xs font-mono opacity-50 animate-float">DEV MODE</div>
            <svg className="w-full h-full" viewBox="0 0 100 100">
              <path d="M0,20 L20,20 L20,0" stroke="#FACC15" strokeWidth="2" fill="none" className="animate-glow" />
            </svg>
          </div>
          <div className="absolute top-0 right-0 w-32 h-32">
            <svg className="w-full h-full" viewBox="0 0 100 100">
              <path d="M80,0 L80,20 L100,20" stroke="#FACC15" strokeWidth="2" fill="none" className="animate-glow delay-75" />
            </svg>
          </div>
          <div className="absolute bottom-0 left-0 w-32 h-32">
            <svg className="w-full h-full" viewBox="0 0 100 100">
              <path d="M0,80 L20,80 L20,100" stroke="#FACC15" strokeWidth="2" fill="none" className="animate-glow delay-150" />
            </svg>
          </div>
          <div className="absolute bottom-0 right-0 w-32 h-32">
            <svg className="w-full h-full" viewBox="0 0 100 100">
              <path d="M100,80 L80,80 L80,100" stroke="#FACC15" strokeWidth="2" fill="none" className="animate-glow delay-300" />
            </svg>
          </div>
        </div>

        {/* Main Dev Panel with enhanced colorful design */}
        <div className="fixed top-4 right-4 p-5 rounded-2xl shadow-2xl z-50 max-w-md"
             style={{
               background: 'linear-gradient(145deg, rgba(88,28,135,0.95), rgba(0,0,0,0.95), rgba(30,58,138,0.95))',
               border: '2px solid',
               borderImage: 'linear-gradient(to bottom right, #facc15, #f97316, #ec4899) 1'
             }}>
          {/* Header with rainbow gradient */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-2xl flex items-center gap-2">
              <span className="animate-spin text-3xl">⚙️</span>
              <span className="bg-gradient-to-r from-yellow-400 via-pink-500 to-cyan-400 bg-clip-text text-transparent font-extrabold">
                DEV MODE
              </span>
            </h3>
            <div className="flex gap-1">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse shadow-lg shadow-green-400/50"></div>
              <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse delay-75 shadow-lg shadow-yellow-400/50"></div>
              <div className="w-3 h-3 bg-red-400 rounded-full animate-pulse delay-150 shadow-lg shadow-red-400/50"></div>
            </div>
          </div>

          {/* System Info with neon glow */}
          <div className="space-y-3 text-sm font-mono">
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-purple-800/50 to-pink-800/50 rounded-lg border border-purple-500/30 backdrop-blur">
              <span className="text-purple-200">Mode:</span>
              <span className="text-cyan-400 font-bold text-base animate-pulse">{mode}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-blue-800/50 to-cyan-800/50 rounded-lg border border-blue-500/30 backdrop-blur">
              <span className="text-blue-200">Previous:</span>
              <span className="text-yellow-400">{previousMode || 'none'}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-green-800/50 to-emerald-800/50 rounded-lg border border-green-500/30 backdrop-blur">
              <span className="text-green-200">Project:</span>
              <span className="text-pink-400 truncate max-w-[150px] font-semibold">{projectName || 'none'}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-orange-800/50 to-red-800/50 rounded-lg border border-orange-500/30 backdrop-blur">
              <span className="text-orange-200">Slug:</span>
              <span className="text-cyan-300 truncate max-w-[150px]">{slug || 'none'}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-indigo-800/50 to-purple-800/50 rounded-lg border border-indigo-500/30 backdrop-blur">
              <span className="text-indigo-200">Link:</span>
              <span className="text-2xl">{createdLink ? '✅' : '❌'}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-pink-800/50 to-rose-800/50 rounded-lg border border-pink-500/30 backdrop-blur">
              <span className="text-pink-200">Files:</span>
              <span className="text-yellow-400 font-bold text-lg">{uploadedFiles.length}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-teal-800/50 to-cyan-800/50 rounded-lg border border-teal-500/30 backdrop-blur">
              <span className="text-teal-200">Upload:</span>
              <span className="text-2xl">{uploadComplete ? '✅' : '❌'}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-yellow-800/50 to-amber-800/50 rounded-lg border border-yellow-500/30 backdrop-blur">
              <span className="text-yellow-200">Selected:</span>
              <span className="text-red-400 font-bold text-lg">{selectedFiles.length}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-emerald-800/50 to-green-800/50 rounded-lg border border-emerald-500/30 backdrop-blur">
              <span className="text-emerald-200">Admin:</span>
              <span className="text-2xl">{isAdmin ? '🔓' : '🔒'}</span>
            </div>
          </div>

          {/* Components section with file paths */}
          <div className="mt-4 pt-4 border-t-2 border-transparent bg-gradient-to-r from-yellow-400/20 via-pink-500/20 to-cyan-400/20 rounded-lg p-3">
            <h4 className="text-base font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent mb-3">
              🎯 Active Components
            </h4>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {components.map((comp, i) => (
                <div key={i} className="group relative">
                  <div className="flex items-center justify-between p-2 bg-black/30 rounded-lg hover:bg-black/50 transition-all cursor-pointer">
                    <span className="text-gray-200 font-medium">{comp.name}</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                      comp.status === 'active' ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white animate-pulse' :
                      comp.status === 'visible' ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white' :
                      comp.status === 'hidden' ? 'bg-gradient-to-r from-gray-600 to-gray-700 text-gray-300' :
                      comp.status === 'dragging' ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white animate-bounce' :
                      comp.status === 'uploading' ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white animate-spin' :
                      'bg-gradient-to-r from-gray-700 to-gray-800 text-gray-400'
                    } shadow-lg`}>
                      {comp.status}
                    </span>
                  </div>
                  {/* File path tooltip */}
                  {componentPaths[comp.name] && (
                    <div className="absolute left-0 right-0 -bottom-6 opacity-0 group-hover:opacity-100 transition-opacity z-[60] pointer-events-none">
                      <div className="bg-black/90 text-xs text-cyan-400 px-2 py-1 rounded font-mono whitespace-nowrap">
                        📍 {componentPaths[comp.name]}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Footer with animations */}
          <div className="mt-4 pt-3 border-t-2 border-transparent bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-yellow-500/20 rounded-lg p-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">FastFiles</span>
                <span className="text-xs text-yellow-400 animate-pulse">v1.2.0</span>
              </div>
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="w-1 h-4 bg-gradient-to-t from-cyan-400 to-purple-400 rounded-full animate-pulse"
                    style={{
                      animationDelay: `${i * 0.1}s`,
                      height: `${Math.random() * 16 + 8}px`
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Landing page specific magic */}
        {mode === 'choose' && (
          <div className="fixed bottom-4 left-4 bg-black bg-opacity-90 text-white p-4 rounded-lg shadow-xl z-50 max-w-sm border border-purple-500">
            <h4 className="text-purple-400 font-bold mb-2 flex items-center gap-2">
              <span>🎯</span> Secret Dev Stats
            </h4>
            <div className="grid grid-cols-2 gap-2 text-xs font-mono">
              <div className="bg-gray-800 p-2 rounded">
                <div className="text-gray-400">Session ID</div>
                <div className="text-green-400">{Math.random().toString(36).substr(2, 9)}</div>
              </div>
              <div className="bg-gray-800 p-2 rounded">
                <div className="text-gray-400">API Status</div>
                <div className="text-green-400">Online</div>
              </div>
              <div className="bg-gray-800 p-2 rounded">
                <div className="text-gray-400">Region</div>
                <div className="text-blue-400">US-WEST</div>
              </div>
              <div className="bg-gray-800 p-2 rounded">
                <div className="text-gray-400">Latency</div>
                <div className="text-yellow-400">{Math.floor(Math.random() * 50 + 10)}ms</div>
              </div>
            </div>
            <div className="mt-2 text-xs text-center text-gray-500">
              Press Ctrl+Shift+D for more
            </div>
          </div>
        )}

        {/* Component Boundary Visualizer */}
        {devMode && (
          <style dangerouslySetInnerHTML={{ __html: `
            [data-component] {
              position: relative;
              outline: 2px dashed rgba(147, 51, 234, 0.3);
              outline-offset: -2px;
              margin-top: 2rem;
            }
            [data-component]:hover {
              outline-color: rgba(147, 51, 234, 0.6);
              background-color: rgba(147, 51, 234, 0.05);
            }
            [data-component]::before {
              content: attr(data-component);
              position: absolute;
              top: -1.8rem;
              left: 0;
              background: rgb(147, 51, 234);
              color: white;
              font-size: 0.7rem;
              padding: 0.2rem 0.5rem;
              border-radius: 0.25rem 0.25rem 0 0;
              font-family: monospace;
              z-index: 50;
              font-weight: bold;
            }
          `}} />
        )}

        {/* Floating particles effect */}
        {devMode && (
          <div className="fixed inset-0 pointer-events-none z-40">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-yellow-400 rounded-full animate-ping"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${i * 0.5}s`,
                  animationDuration: '3s'
                }}
              />
            ))}
          </div>
        )}

        {/* Matrix Rain Effect for Ultimate Dev Mode */}
        {devMode && mode === 'choose' && (
          <div className="fixed inset-0 pointer-events-none z-30 overflow-hidden opacity-10">
            {[...Array(15)].map((_, i) => (
              <div
                key={`matrix-${i}`}
                className="absolute text-green-400 font-mono text-xs animate-matrix"
                style={{
                  left: `${i * 7}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  animationDuration: `${3 + Math.random() * 2}s`
                }}
              >
                {Array(20).fill(0).map((_, j) => (
                  <div key={j}>{Math.random() > 0.5 ? '1' : '0'}</div>
                ))}
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  // CHOOSE MODE
  if (mode === 'choose') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 relative overflow-hidden">
        <DevModePanel />
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-400 rounded-full filter blur-3xl"></div>
        </div>

        <div className="relative z-10">
          {/* Header with branding */}
          <header className="px-4 py-6">
            <div className="max-w-7xl mx-auto flex justify-between items-center">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-xl">F</span>
                </div>
                <span className="font-bold text-xl text-gray-900">FastFiles</span>
              </div>
              <nav className="hidden md:flex items-center gap-6 text-sm">
                <a href="/changelog" className="text-gray-600 hover:text-gray-900">What's New</a>
                <a href="/architecture-flow" className="text-gray-600 hover:text-gray-900">How it Works</a>
                {isAdmin && (
                  <a href="/admin" className="text-blue-600 hover:text-blue-700 font-medium">Admin Panel</a>
                )}
              </nav>
            </div>
          </header>

          {/* Hero Section */}
          <div className="flex items-center justify-center px-4 py-12">
            <div className="text-center max-w-4xl">
              {/* Trust badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-full text-sm text-green-700 mb-6">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                <span>Trusted by thousands • 100% Free</span>
              </div>

              {/* Dev Mode Easter Egg on Landing */}
              {devMode && (
                <div className="mb-8 animate-bounce">
                  <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full text-white font-bold shadow-lg">
                    <span className="text-xl">🎉</span>
                    <span>DEV MODE ACTIVATED!</span>
                    <span className="text-xl">🚀</span>
                  </div>
                  <div className="mt-2 text-sm text-gray-600">
                    You found the secret developer mode! Explore with enhanced debugging.
                  </div>
                </div>
              )}

              <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Share Files Instantly
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 mb-4 max-w-2xl mx-auto">
                The fastest way to send and receive files. No signup, no limits, just drag and drop.
              </p>

              {/* Social proof */}
              <div className="flex items-center justify-center gap-8 mb-12 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">⚡</span>
                  <span>Instant sharing</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🔒</span>
                  <span>Secure & Private</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🌍</span>
                  <span>Works everywhere</span>
                </div>
              </div>

              {/* Action buttons */}
              <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto mb-12">
                <button
                  onClick={() => setMode('send')}
                  className="group relative bg-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all border-2 border-transparent hover:border-blue-500 transform hover:-translate-y-1"
                >
                  <div className="absolute -top-3 -right-3 px-3 py-1 bg-blue-500 text-white text-xs rounded-full font-medium">Popular</div>
                  <div className="text-5xl mb-4">📤</div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">Send Files</h2>
                  <p className="text-gray-600">Upload and share with a simple link</p>
                  <div className="mt-4 text-blue-600 font-medium group-hover:underline">Start sending →</div>
                </button>

                <button
                  onClick={() => setMode('receive')}
                  className="group relative bg-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all border-2 border-transparent hover:border-purple-500 transform hover:-translate-y-1"
                >
                  <div className="text-5xl mb-4">📥</div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">Receive Files</h2>
                  <p className="text-gray-600">Create a dropbox for others</p>
                  <div className="mt-4 text-purple-600 font-medium group-hover:underline">Create receiver →</div>
                </button>
              </div>

              {/* Trust indicators */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 max-w-3xl mx-auto">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                  <div>
                    <div className="text-3xl font-bold text-gray-900">10K+</div>
                    <div className="text-sm text-gray-500">Files Shared</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-gray-900">5GB</div>
                    <div className="text-sm text-gray-500">Max File Size</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-gray-900">100%</div>
                    <div className="text-sm text-gray-500">Free Forever</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-gray-900">256-bit</div>
                    <div className="text-sm text-gray-500">Encryption</div>
                  </div>
                </div>
              </div>

              {/* Security badges */}
              <div className="mt-12 flex flex-col items-center gap-4">
                <div className="flex items-center gap-6 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>No signup required</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>End-to-end secure</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Works on all devices</span>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <img src="https://img.shields.io/badge/Powered%20by-Cloudflare-F38020?style=flat-square" alt="Powered by Cloudflare" className="h-6" />
                  <img src="https://img.shields.io/badge/Storage-R2-5865F2?style=flat-square" alt="R2 Storage" className="h-6" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // SEND MODE
  if (mode === 'send') {
    // Step 1: Name the project
    if (!createdLink) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
          <DevModePanel />
          <div className="bg-white rounded-3xl shadow-xl p-8 max-w-lg w-full border border-blue-100">
            <button onClick={() => setMode('choose')} className="mb-6 text-gray-500 hover:text-gray-700 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>

            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-4">
                <span className="text-3xl">📤</span>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Send Files</h2>
              <p className="text-gray-600">Name your share to get started</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What are you sharing?
                </label>
                <input
                  type="text"
                  value={projectName}
                  onChange={handleProjectNameChange}
                  placeholder="e.g., Vacation Photos, Project Files, Documents"
                  className="w-full px-4 py-3 text-lg border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
                  autoFocus
                />
              </div>

              {projectName && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-xs font-medium text-blue-600 mb-1">Your unique link:</p>
                  <p className="font-mono text-sm text-gray-700 break-all">
                    fastfiles.app/files/<span className="font-bold text-blue-600">{slug}</span>
                  </p>
                </div>
              )}

              <button
                onClick={createLink}
                disabled={!projectName || isCreating}
                className={`w-full py-4 text-lg font-medium rounded-xl transition-all flex items-center justify-center gap-2 ${
                  projectName && !isCreating
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-lg'
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                {isCreating ? (
                  <>
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  <>
                    Continue
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </>
                )}
              </button>

              <div className="text-center text-xs text-gray-500 pt-2">
                <span className="inline-flex items-center gap-1">
                  <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Secure link • No expiration • Free forever
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Step 2: Upload files
    if (!uploadComplete) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
          <DevModePanel />
          <div className="bg-white rounded-3xl shadow-xl p-8 max-w-2xl w-full border border-blue-100">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl mb-4">
                <span className="text-3xl">📤</span>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Upload Your Files</h2>
              <p className="text-gray-600">Add files to your share: <span className="font-semibold text-blue-600">{projectName}</span></p>
            </div>

            <div
              className={`relative border-3 border-dashed rounded-2xl p-12 text-center transition-all cursor-pointer group focus:outline-none focus:ring-4 focus:ring-blue-400 focus:ring-opacity-50 ${
                isDragging
                  ? 'border-blue-500 bg-blue-100 scale-105'
                  : 'border-blue-300 bg-gradient-to-br from-blue-50 to-white hover:from-blue-100 hover:to-white'
              }`}
              onClick={() => document.getElementById('file-input')?.click()}
              onPaste={(e) => {
                e.preventDefault();
                const items = e.clipboardData?.items;
                if (!items) return;

                const pastedFiles: File[] = [];
                for (let i = 0; i < items.length; i++) {
                  const item = items[i];
                  if (item.kind === 'file') {
                    const file = item.getAsFile();
                    if (file) {
                      pastedFiles.push(file);
                    }
                  }
                }

                if (pastedFiles.length > 0) {
                  setSelectedFiles(prev => [...prev, ...pastedFiles]);
                }
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(true);
              }}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(false);

                const files = Array.from(e.dataTransfer.files);
                if (files.length > 0) {
                  setSelectedFiles(files);
                }
              }}
              tabIndex={0}
            >
              <div className="absolute inset-0 bg-blue-600 opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity"></div>
              <div className="relative">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full shadow-lg mb-4 group-hover:scale-110 transition-transform">
                  <span className="text-4xl">📁</span>
                </div>
                <p className="text-xl font-semibold text-gray-800 mb-2">
                  {isDragging ? 'Release to upload!' : 'Drop, paste, or click to add files'}
                </p>
                <p className="text-gray-600 mb-4">Support for images, documents, videos, and more</p>
                <p className="text-sm text-gray-500">Maximum file size: 5GB • Press Ctrl+V/Cmd+V to paste files</p>
              </div>
              <input
                id="file-input"
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            {selectedFiles.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">Ready to upload</h3>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                    {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 space-y-2 max-h-40 overflow-y-auto border border-gray-200">
                  {selectedFiles.map((file, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-white rounded-lg">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="text-xl">📄</span>
                        <span className="truncate font-medium text-gray-700">{file.name}</span>
                      </div>
                      <span className="text-sm text-gray-500 whitespace-nowrap ml-2">
                        {(file.size / 1024 / 1024).toFixed(1)} MB
                      </span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={uploadFiles}
                  disabled={isUploading}
                  className="w-full mt-6 py-4 bg-gradient-to-r from-green-600 to-green-700 text-white text-lg font-semibold rounded-xl hover:from-green-700 hover:to-green-800 transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden"
                >
                  {isUploading && (
                    <div
                      className="absolute inset-0 bg-green-800 opacity-30"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  )}
                  <div className="relative flex items-center gap-2">
                    {isUploading ? (
                      <>
                        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="flex flex-col items-center">
                          <span>Uploading... {uploadProgress}%</span>
                          {currentUploadFile && <span className="text-xs opacity-75 truncate max-w-48">{currentUploadFile}</span>}
                        </span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <span>Upload Files</span>
                      </>
                    )}
                  </div>
                </button>
              </div>
            )}

            <button
              onClick={() => setUploadComplete(true)}
              className="w-full mt-4 py-3 text-gray-500 hover:text-gray-700 transition-colors flex items-center justify-center gap-2"
            >
              Skip for now
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      );
    }

    // Step 3: Share options
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
        <DevModePanel />
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-2xl w-full border border-green-100">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-100 to-green-200 rounded-full mb-4">
              <span className="text-5xl">🎉</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Your Share is Ready!</h2>
            <p className="text-gray-600">Share this link to give access to your files</p>
          </div>

          {/* Prominent Share Button for Mobile */}
          <button
            onClick={copyLink}
            className="w-full mb-4 py-4 bg-blue-600 text-white text-lg font-semibold rounded-xl hover:bg-blue-700 transition-all shadow-md flex items-center justify-center gap-2"
          >
            <span className="text-xl">📋</span>
            <span>Tap to Copy Share Link</span>
          </button>

          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <div className="flex gap-2">
              <input
                type="text"
                value={createdLink}
                readOnly
                className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg font-mono text-sm"
              />
              <button
                id="copy-btn"
                onClick={copyLink}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Copy Link
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6" data-component="ShareButtons">
            <div className="relative group">
              <button
                onClick={() => shareViaEmail(false)}
                className="w-full flex items-center justify-center gap-2 p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 transition-colors">
                <span className="text-2xl">📧</span>
                <span>Email Link</span>
              </button>
              <button
                onClick={() => shareViaEmail(true)}
                className="absolute top-1 right-1 px-2 py-1 text-xs bg-white border border-gray-200 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-gray-50">
                Gmail
              </button>
            </div>
            <button
              onClick={shareViaWhatsApp}
              className="flex items-center justify-center gap-2 p-4 border-2 border-gray-200 rounded-xl hover:border-green-500 transition-colors">
              <span className="text-2xl">💬</span>
              <span>WhatsApp</span>
            </button>
            <button
              onClick={shareViaSMS}
              className="flex items-center justify-center gap-2 p-4 border-2 border-gray-200 rounded-xl hover:border-blue-400 transition-colors">
              <span className="text-2xl">📱</span>
              <span>Text Message</span>
            </button>
            <button
              onClick={showQRCode}
              className="flex items-center justify-center gap-2 p-4 border-2 border-gray-200 rounded-xl hover:border-purple-500 transition-colors">
              <span className="text-2xl">📲</span>
              <span>QR Code</span>
            </button>
          </div>
          <div id="qr-container"></div>

          <div className="flex gap-4">
            <a
              href={createdLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 text-center py-3 bg-green-600 text-white rounded-xl hover:bg-green-700"
            >
              View Upload Page
            </a>
            <button
              onClick={() => {
                setPreviousMode('send');
                setMode('manage');
              }}
              className="flex-1 text-center py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50"
            >
              View Uploaded Files
            </button>
          </div>
        </div>
      </div>
    );
  }

  // RECEIVE MODE
  if (mode === 'receive') {
    if (!createdLink) {
      return (
        <div className="min-h-screen bg-white flex items-center justify-center p-4">
          <DevModePanel />
          <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-lg w-full" data-component="ReceiveModal">
            <button onClick={() => setMode('choose')} className="mb-6 text-gray-500 hover:text-gray-700" data-component="BackButton">
              ← Back
            </button>

            <div className="text-4xl mb-4">📥</div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2" data-component="ReceiveTitle">Receive Files</h2>
            <p className="text-gray-600 mb-6">What files are you collecting?</p>

            <input
              type="text"
              value={projectName}
              onChange={handleProjectNameChange}
              placeholder="e.g., Wedding Photos, Event Registration, Homework"
              className="w-full px-4 py-3 text-lg border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none mb-3"
              autoFocus
              data-component="ReceiverNameInput"
            />

            {projectName && (
              <div className="mb-6 p-3 bg-gray-50 rounded-lg" data-component="LinkPreview">
                <p className="text-xs text-gray-500 mb-1">Your link will be:</p>
                <p className="font-mono text-sm text-gray-700">
                  fastfiles.app/files/<span className="font-bold">{slug}</span>
                </p>
              </div>
            )}

            <button
              onClick={createLink}
              disabled={!projectName || isCreating}
              className={`w-full py-4 text-lg font-medium rounded-xl transition-all ${
                projectName && !isCreating
                  ? 'bg-purple-600 text-white hover:bg-purple-700'
                  : 'bg-gray-200 text-gray-400'
              }`}
              data-component="CreateLinkButton"
            >
              {isCreating ? 'Creating...' : 'Create Upload Link'}
            </button>
          </div>
        </div>
      );
    }

    // Share the receive link
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <DevModePanel />
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-2xl w-full">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">✨</div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Your Upload Link is Ready!</h2>
            <p className="text-gray-600">Share this link to start receiving files</p>
          </div>

          {/* Prominent Share Button for Mobile */}
          <button
            onClick={copyLink}
            className="w-full mb-4 py-4 bg-purple-600 text-white text-lg font-semibold rounded-xl hover:bg-purple-700 transition-all shadow-md flex items-center justify-center gap-2"
          >
            <span className="text-xl">📋</span>
            <span>Tap to Copy Share Link</span>
          </button>

          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <div className="flex gap-2">
              <input
                type="text"
                value={createdLink}
                readOnly
                className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg font-mono text-sm"
              />
              <button
                id="copy-btn"
                onClick={copyLink}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Copy Link
              </button>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-6">
            <h3 className="font-semibold text-gray-900 mb-2">📮 How it works:</h3>
            <ol className="space-y-2 text-gray-700">
              <li>1. Share this link with anyone who needs to send you files</li>
              <li>2. They can upload files without creating an account</li>
              <li>3. You'll receive files instantly</li>
              <li>4. Download files individually or all at once</li>
            </ol>
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => {
                setPreviousMode('receive');
                setMode('manage');
              }}
              className="flex-1 text-center py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700"
            >
              View Uploaded Files
            </button>
            <button
              onClick={() => { setMode('choose'); setCreatedLink(''); setProjectName(''); }}
              className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50"
            >
              Create Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Helper functions for file management
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  // MANAGE MODE - View uploaded files
  if (mode === 'manage') {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <DevModePanel />

        <div className="max-w-6xl mx-auto" data-component="PageContainer">
          <button
            data-component="BackButton"
            onClick={() => {
              if (previousMode && createdLink) {
                // Go back to the share screen (send/receive completed state)
                setMode(previousMode);
                setUploadComplete(true);
              } else {
                // Reset to home
                setMode('choose');
                setCreatedLink('');
                setProjectName('');
                setSlug('');
                setUploadComplete(false);
                setPreviousMode(null);
              }
            }}
            className="mb-6 text-gray-500 hover:text-gray-700"
          >
            ← Back to Share Options
          </button>

          <div className="bg-white rounded-3xl shadow-2xl p-8" data-component="ManagePanel">
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-gray-800 mb-2">{projectName}</h2>
              <p className="text-gray-600">{uploadedFiles.length} file{uploadedFiles.length !== 1 ? 's' : ''} uploaded</p>
            </div>

            {/* Share Link Section */}
            <div className="mb-6 p-4 bg-blue-50 rounded-xl" data-component="ShareLinkBox">
              <p className="text-sm text-gray-600 mb-2">Share this link to receive more files:</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={createdLink}
                  readOnly
                  className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg font-mono text-sm"
                />
                <button
                  onClick={copyLink}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Copy Link
                </button>
              </div>
            </div>

            <div data-component="FilesList">
            {loadingFiles ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : uploadedFiles.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📭</div>
                <p className="text-xl text-gray-600">No files uploaded yet</p>
                <p className="text-gray-500 mt-2">Share your upload link to start receiving files</p>
                <div className="mt-6 p-4 bg-gray-50 rounded-xl inline-block">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={createdLink}
                      readOnly
                      className="px-3 py-2 bg-white border border-gray-300 rounded-lg font-mono text-sm"
                    />
                    <button
                      onClick={copyLink}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Copy Link
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {uploadedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">📄</span>
                      <div>
                        <p className="font-medium text-gray-900">{file.name}</p>
                        <p className="text-sm text-gray-500">
                          {formatFileSize(file.size)} • {formatDate(file.uploadedAt)}
                          {file.uploaderName && ` • From ${file.uploaderName}`}
                        </p>
                      </div>
                    </div>
                    {file.url && (
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Download
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200" data-component="ActionButtons">
              <div className="flex gap-3">
                <button
                  data-component="RefreshButton"
                  onClick={loadUploadedFiles}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  🔄 Refresh
                </button>
                <button
                  data-component="UploadMoreButton"
                  onClick={() => {
                    // Determine which mode to return to based on previousMode
                    if (previousMode === 'send') {
                      // Go back to send mode, step 2 (upload files)
                      setMode('send');
                      setUploadComplete(false);
                      setSelectedFiles([]);
                      setUploadProgress(0);
                      setIsUploading(false);
                    } else if (previousMode === 'receive') {
                      // For receive mode, go to the upload page URL
                      window.open(createdLink, '_blank');
                    } else {
                      // Fallback: go to send mode for uploading
                      setMode('send');
                      setUploadComplete(false);
                      setSelectedFiles([]);
                      setUploadProgress(0);
                      setIsUploading(false);
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700"
                >
                  Upload More Files
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}